import type { Me, MyAward, StockAdminQueue, WaitingOnOthers } from '@myrewards/contracts';
import {
  DONE_FOR_YOU_HREF,
  doneTodayMeta,
  doneTodaySentence,
  type DoneToday,
} from '../acting-for/doneWords';
import {
  ALL_OF_THEM_HREF,
  othersMeta,
  othersNothingWords,
  othersOnHome,
  othersWhose,
} from '../acting-for/words';
import { spendSearch } from '../api/insights';
import { missionPhotos, peoplePhotos, type HeroPhotos } from '../assets';
import { QUEUE_VIEW, WORK_HREF } from '../ops/labels';
import { ROLE_LABEL } from '../session/presenter';
import type { HeroAction, HeroStat } from '../shell';
import {
  daysBetween,
  formatCount,
  formatDate,
  formatMoney,
  formatWeekdayDate,
  plural,
  type ScopedLabel,
} from '../ui';
import { AWARD_TYPE_LABEL, capitalize, countWord, greeting, type PoolPosition } from './labels';
import type { ExecutiveModel, HomeModel, RequesterModel, SectorPanelModel } from './model';

export interface HomeHero {
  eyebrow: string;
  eyebrowTone: 'cyan' | 'gold';
  title: string;
  sub: string;
  actions?: readonly [HeroAction] | readonly [HeroAction, HeroAction];
  stats: readonly HeroStat[];
  photos: HeroPhotos;
}

/** The FR1 hero's first action jumps to the "How recognition works" card on the page. */
export const HOW_IT_WORKS_HREF = '#how-recognition-works';
/** FR1: "Take the quick tour" starts the training walkthrough in practice mode (APP-081). */
export const QUICK_TOUR_HREF = '/training?tour=start';
/** D1: the hero's "My entries" moves focus to the delegate's My entries card. */
export const MY_ENTRIES_HREF = '#my-entries';

const scoped = (scope: string, what: string): ScopedLabel => `${scope} · ${what}`;

/**
 * APP-148: what acting for others adds to a hero. A Stock Admin's Waiting on others, while either
 * switch is on (D2); what was done for an Executive or a Sector Lead today, when there's something.
 */
export interface HomeActing {
  others: WaitingOnOthers | null;
  doneToday: DoneToday | null;
}

const NO_ACTING: HomeActing = { others: null, doneToday: null };

/** The Home board's number: "Executives and Sector Leads · waiting", to Approvals. */
function othersStat(others: WaitingOnOthers): HeroStat {
  return {
    label: scoped(othersWhose(others.switches), 'waiting'),
    value: formatCount(othersOnHome(others).length),
    meta: othersMeta(others),
    href: ALL_OF_THEM_HREF,
  };
}

/** "Five things are waiting on the Executives and Sector Leads. When one of them asks …". */
function othersSentence(others: WaitingOnOthers): string {
  const n = othersOnHome(others).length;
  const { executives, sectorLeads } = others.switches;
  // R142: with a switch off it says what isn't listed, never that nothing waits on that role.
  if (n === 0) return othersNothingWords({ executives, sectorLeads }).hero;
  return `${capitalize(countWord(n))} ${n === 1 ? 'thing is' : 'things are'} waiting on the ${othersWhose(others.switches)}. When one of them asks you to do it for them, you can.`;
}

/** The ExecHome and LeadHome boards' number: "Done for you · today", by whom and when. */
function doneStat(done: DoneToday): HeroStat {
  return {
    label: scoped('Done for you', 'today'),
    value: formatCount(done.count),
    meta: doneTodayMeta(done),
    href: DONE_FOR_YOU_HREF,
  };
}

/** Where "Recognize" goes: only to what this person can start (Recognize options). */
export function recognizeAction(model: HomeModel): HeroAction | null {
  if (model.options.entries.length > 0) {
    return { label: 'Recognize', href: '/recognize', tone: 'cyan', icon: 'plus' };
  }
  if (model.options.spotNomination) {
    return { label: 'Nominate someone', href: '/recognize/spot', tone: 'cyan', icon: 'plus' };
  }
  return null;
}

/**
 * Where HOME2's numbers lead (APP-044 §54): each is the link to the list it counts, as SA1's are.
 * Waiting for a claim to the queue, the reader's claims to My queue, and the Shareworks rows to
 * the worklist. Links stay at zero: each target has its own designed empty state.
 */
export const HOME2_HREF = {
  queue: QUEUE_VIEW.queue.href,
  mine: QUEUE_VIEW.mine.href,
  shareworks: WORK_HREF.shareworks,
} as const;

function queueHero(
  me: Me,
  model: HomeModel,
  queue: StockAdminQueue,
  others: WaitingOnOthers | null,
): HomeHero {
  const mine = queue.mine.checks + queue.mine.letters;
  const active = queue.rows.length;
  // The worklist's own count of rows left (APP-067 §13), as SA1's hero and SA5 say it.
  const shareworks = queue.shareworksRows;
  const empty = queue.rows.length === 0 && mine === 0;
  // An empty queue is good news once awards have come through (FR4); on day one it's ready.
  const caughtUp = empty && model.queueHistory === true;
  const stats: HeroStat[] = [
    {
      label: 'Queue · all sectors',
      value: formatCount(active),
      meta:
        active === 0 ? 'No active submitted awards' : 'All active stages · claimed and unclaimed',
      href: HOME2_HREF.queue,
    },
    {
      label: 'My queue · all sectors',
      value: formatCount(mine),
      meta:
        mine === 0
          ? 'Nothing claimed yet'
          : `${plural(queue.mine.checks, 'check', 'checks')} · ${plural(queue.mine.letters, 'letter', 'letters')}`,
      href: HOME2_HREF.mine,
    },
  ];
  if (queue.views.some((v) => v.id === 'shareworks')) {
    stats.push({
      label: 'Shareworks · rows to enter',
      value: formatCount(shareworks),
      meta: shareworks === 0 ? 'No grants to enter' : 'Grants waiting to be entered',
      href: HOME2_HREF.shareworks,
    });
  }
  // APP-148 D2 (the Home board): the Executives' and Sector Leads' work, second, so Slim keeps it.
  if (others !== null) stats.splice(1, 0, othersStat(others));
  const sub = caughtUp
    ? 'Nothing is waiting for you right now. We’ll email you the moment a request reaches the queue.'
    : empty
      ? 'Your queue is ready for its first request. Claim it, check it, send it on; your claims show to the whole team.'
      : 'Claim a request, check it, send it on. Your claims show to the whole team.';
  return {
    eyebrow: `Stock administration · ${formatWeekdayDate(model.asOf)}`,
    eyebrowTone: caughtUp ? 'gold' : 'cyan',
    title: caughtUp ? `All caught up, ${me.person.greetingName}.` : 'Your work queue',
    sub: others === null ? sub : `${sub} ${othersSentence(others)}`,
    ...(others !== null && {
      actions: [
        { label: 'Open the queue', href: HOME2_HREF.queue, tone: 'cyan' },
        { label: 'Waiting on others', href: ALL_OF_THEM_HREF },
      ] as const,
    }),
    stats,
    photos: missionPhotos,
  };
}

// --- Executive (X1, FR3) ----------------------------------------------------------------------

const BUDGETS_HREF = '/manage/executive/budget-pools';

/**
 * Where X1's numbers lead (APP-044, as built 2026-10-20): each is a link to the list or table it
 * counts. The pools' cash and options left, and how many pools are set, to Budget pools' table for
 * the year, pool by pool; the PAA/TAA pool's awarded to Spend for that pool and year, which breaks
 * it down by award and person; the approvals to Approvals, which lists them by kind.
 */
export const X1_POOLS_HREF = `${BUDGETS_HREF}#budget-pools`;
export const X1_APPROVALS_HREF = '/approvals';
export const x1PoolSpendHref = (poolId: string, fiscalYear: number): string =>
  `/insights/spend${spendSearch({ fiscalYear, poolId })}`;

function approvalsMeta(a: ExecutiveModel['approvals']): string {
  if (a.total === 0) return 'Nothing waiting · overages, PAAs and TAAs come here';
  const parts: string[] = [];
  if (a.overage > 0) parts.push(plural(a.overage, 'overage', 'overages'));
  if (a.TAA > 0) parts.push(`${formatCount(a.TAA)} TAA`);
  if (a.PAA > 0) parts.push(`${formatCount(a.PAA)} PAA`);
  if (a.EDB > 0) parts.push(plural(a.EDB, 'request', 'requests'));
  return parts.join(' · ');
}

function executiveHero(me: Me, ex: ExecutiveModel, now: Date, done: DoneToday | null): HomeHero {
  const name = me.person.greetingName;
  const fy = String(ex.fiscalYear);
  const total = ex.pools.length;
  const approvals: HeroStat = {
    label: 'Executive group · approvals waiting',
    value: plural(ex.approvals.total, 'approval', 'approvals'),
    meta: approvalsMeta(ex.approvals),
    tone: 'gold',
    href: X1_APPROVALS_HREF,
  };
  // APP-148 (the ExecHome board): what was done for them today follows what's waiting.
  const doneStats = done === null ? [] : [doneStat(done)];
  const waitingSentence =
    ex.approvals.total === 0
      ? done === null
        ? 'When something needs an Executive, it shows up here first.'
        : `Nothing is waiting on you. ${doneTodaySentence(done)}`
      : `${capitalize(countWord(ex.approvals.total))} ${ex.approvals.total === 1 ? 'approval is' : 'approvals are'} waiting. ${done === null ? 'Any one Executive can act; the others see it’s done.' : doneTodaySentence(done)}`;

  // No pools to show (Preview keeps budgets with the people who own them): approvals only.
  if (total === 0) {
    return {
      eyebrow: 'Executive · every pool, every sector',
      eyebrowTone: 'cyan',
      title: `${greeting(now)}, ${name}.`,
      sub: waitingSentence,
      actions: [
        ex.approvals.total > 0
          ? { label: 'Review approvals', href: '/approvals', tone: 'cyan' }
          : { label: 'Budgets', href: BUDGETS_HREF, tone: 'cyan' },
      ],
      stats: [approvals, ...doneStats],
      photos: missionPhotos,
    };
  }

  // FR3: before anything is awarded, finish the pools first.
  if (ex.nothingYet && ex.poolsSet < total) {
    // Name the pool when only one is missing (QA-031): "Set PAA/TAA budget".
    const unset = ex.pools.filter((p) => p.budget === null);
    const only = unset.length === 1 ? unset[0] : undefined;
    return {
      eyebrow: 'Executive · before go-live',
      eyebrowTone: 'cyan',
      title: `Let’s get ${fy} ready.`,
      sub:
        ex.poolsSet === 0
          ? `Start by setting the ${fy} budget pools. Nothing has been awarded yet; when something needs an Executive, it shows up here first.`
          : only !== undefined
            ? `${capitalize(countWord(ex.poolsSet))} of ${countWord(total)} pools are set. The ${only.pool.name} pool has no budget yet; a few decisions and MyRewards is ready for the Sector Leads and their teams.`
            : `${capitalize(countWord(ex.poolsSet))} of ${countWord(total)} pools are set. A few decisions and MyRewards is ready for the Sector Leads and their teams.`,
      actions: [
        {
          label:
            ex.poolsSet === 0
              ? 'Set all budget pools'
              : only !== undefined
                ? `Set ${only.pool.name} budget`
                : 'Set the missing budgets',
          href: BUDGETS_HREF,
          tone: 'cyan',
        },
        { label: 'Review settings', href: '/manage/executive/values' },
      ],
      stats: [
        {
          label: scoped('Pools set', fy),
          value: `${formatCount(ex.poolsSet)} of ${formatCount(total)}`,
          meta:
            ex.discretionary.budget === null
              ? 'No budgets yet'
              : `${formatMoney(ex.discretionary.budget.cashCents)} and ${plural(ex.discretionary.budget.options, 'option', 'options')} set`,
          href: X1_POOLS_HREF,
        },
        approvals,
        ...doneStats,
      ],
      photos: missionPhotos,
    };
  }

  const d = ex.discretionary;
  const pools = plural(d.pools, 'pool', 'pools');
  const stats: HeroStat[] =
    d.budget === null || d.left === null
      ? [
          {
            label: scoped('Discretionary pools', `cash left ${fy}`),
            value: 'Not set',
            notSet: {
              message: `Set it in Executive › Budget pools`,
              actionLabel: 'Set budgets',
              href: BUDGETS_HREF,
            },
          },
        ]
      : [
          {
            label: scoped('Discretionary pools', `cash left ${fy}`),
            value: formatMoney(d.left.cashCents),
            meta:
              d.awarded.cashCents === 0
                ? `$0 awarded · the full ${formatMoney(d.budget.cashCents)} available · ${pools}`
                : `${formatMoney(d.awarded.cashCents)} awarded · of ${formatMoney(d.budget.cashCents)} · ${pools}`,
            href: X1_POOLS_HREF,
          },
          {
            label: scoped('Discretionary pools', `options left ${fy}`),
            value: formatCount(d.left.options),
            meta:
              d.awarded.options === 0
                ? `0 granted · all ${formatCount(d.budget.options)} available · ${pools}`
                : `${formatCount(d.awarded.options)} granted · of ${formatCount(d.budget.options)} · ${pools}`,
            href: X1_POOLS_HREF,
          },
        ];
  const paa = ex.paaTaa;
  if (paa) {
    const awarded = paa.paid.cashCents + paa.inProgress.cashCents;
    stats.push(
      paa.budget === null
        ? {
            label: scoped('PAA/TAA pool', `budget ${fy}`),
            value: 'Not set',
            notSet: {
              message: 'Set it in Executive › Budget pools',
              actionLabel: 'Set budget',
              href: BUDGETS_HREF,
            },
          }
        : {
            label: scoped('PAA/TAA pool', `awarded ${fy}`),
            value: formatMoney(awarded),
            meta:
              awarded === 0
                ? `Nothing awarded yet · of ${formatMoney(paa.budget.cashCents)}`
                : `Of ${formatMoney(paa.budget.cashCents)}`,
            href: x1PoolSpendHref(paa.pool.id, ex.fiscalYear),
          },
    );
  }
  stats.push(approvals, ...doneStats);
  const allUnset = ex.poolsSet === 0;
  return {
    eyebrow: 'Executive · every pool, every sector',
    eyebrowTone: 'cyan',
    title: ex.nothingYet ? `Welcome, ${name}.` : `${greeting(now)}, ${name}.`,
    sub: ex.nothingYet
      ? `Every ${fy} pool is set and nothing has been awarded yet. When something needs an Executive, it shows up here first.`
      : waitingSentence,
    actions:
      ex.approvals.total > 0
        ? [
            { label: 'Review approvals', href: '/approvals', tone: 'cyan' },
            { label: 'Budgets', href: BUDGETS_HREF },
          ]
        : [
            {
              label: allUnset ? 'Set all budget pools' : 'Budgets',
              href: BUDGETS_HREF,
              tone: 'cyan',
            },
          ],
    stats,
    photos: missionPhotos,
  };
}

// --- Requester (R1) ---------------------------------------------------------------------------

/** The R1 hero's "How it works" scrolls to its card on the page. */
export const REQUEST_HOW_HREF = '#how-requests-work';

function requesterHero(me: Me, r: RequesterModel, now: Date): HomeHero {
  const name = me.person.greetingName;
  const newRequest: HeroAction = {
    label: 'Start an award',
    href: '/recognize',
    tone: 'cyan',
    icon: 'plus',
  };
  const waiting = r.waitingOnExecutives;
  const first = waiting[0];
  const a = r.awarded;
  return {
    eyebrow: 'Executive delegate',
    eyebrowTone: 'cyan',
    title: r.nothingYet ? `Welcome, ${name}.` : `${greeting(now)}, ${name}.`,
    sub: r.nothingYet
      ? 'When the Executive you work for wants to recognize someone, start the award here. We take it to the Executives for you.'
      : waiting.length === 0
        ? 'Nothing is waiting on the Executives. Start an award whenever you need to.'
        : `${waiting.length === 1 ? 'One request is' : `${capitalize(countWord(waiting.length))} requests are`} waiting on the Executives. When they decide, record what they approved here and attach the proof.`,
    // Recording a decision has one place, Waiting on you, right below (QA-031).
    actions: [newRequest, { label: 'How it works', href: REQUEST_HOW_HREF }],
    stats: [
      {
        label: 'Your requests · waiting on the Executives',
        value: plural(waiting.length, 'request', 'requests'),
        meta: first
          ? `${first.displayId ?? 'Draft'} · decision outside the app`
          : 'Nothing out yet · one notice reaches every Executive',
      },
      {
        label: scoped('Your requests', `awarded ${String(r.year)}`),
        value: formatMoney(a.cashCents),
        meta:
          a.requests === 0
            ? 'Your first request starts this count'
            : [
                plural(a.people, 'person', 'people'),
                a.options > 0 ? plural(a.options, 'option', 'options') : null,
                plural(a.requests, 'request', 'requests'),
              ]
                .filter((part): part is string => part !== null)
                .join(' · '),
      },
    ],
    photos: peoplePhotos,
  };
}

/** The hero's two pool numbers (glass blocks), scoped to the sector and year. */
export function poolStats(sector: string, year: number, position: PoolPosition | null): HeroStat[] {
  const fy = String(year);
  const budget = position?.budget ?? null;
  const remaining = position?.remaining ?? null;
  if (!position || !budget || !remaining) {
    // FR2: one not-set block replaces the two budget blocks until the pool is set. The options
    // budget is set with the cash, so a second "Not set" tile would only repeat it.
    return [
      {
        label: scoped(sector, `budget ${fy}`),
        value: 'Not set',
        notSet: {
          message: `Waiting on the Executives to set the ${fy} cash and options budget. You can draft awards meanwhile.`,
          actionLabel: 'Start a draft',
          href: '/recognize',
        },
      },
    ];
  }
  const awarded = position.paid.cashCents + position.inProgress.cashCents;
  const given = position.paid.options + position.inProgress.options;
  return [
    {
      label: scoped(sector, `cash left ${fy}`),
      value: formatMoney(remaining.cashCents),
      meta:
        remaining.cashCents < 0
          ? `Over budget · ${formatMoney(awarded)} awarded of ${formatMoney(budget.cashCents)}`
          : awarded === 0
            ? `$0 awarded · full ${formatMoney(budget.cashCents)} available`
            : `${formatMoney(awarded)} awarded · of ${formatMoney(budget.cashCents)}`,
    },
    {
      label: scoped(sector, `options left ${fy}`),
      value: formatCount(remaining.options),
      meta:
        given === 0
          ? `None granted yet · of ${formatCount(budget.options)}`
          : `${formatCount(given)} given · of ${formatCount(budget.options)}`,
    },
  ];
}

/** " One quick setup step is waiting on you: your preferences. Then recognize …" */
function setupSentence(setup: number, then: string): string {
  const lower = `${then.charAt(0).toLowerCase()}${then.slice(1)}`;
  if (setup === 0) return ` ${then}`;
  if (setup === 1)
    return ` One quick setup step is waiting on you: your preferences. Then ${lower}`;
  return ` ${capitalize(countWord(setup))} quick setup steps are waiting on you. Then ${lower}`;
}

const DECISIONS: ReadonlySet<string> = new Set([
  'approve_delegate_entry',
  'executive_decision',
  'approve_overage',
]);

function blendedHero(
  me: Me,
  model: HomeModel,
  panel: SectorPanelModel,
  now: Date,
  done: DoneToday | null,
): HomeHero {
  const name = me.person.greetingName;
  const decisions = model.waiting.filter((w) => DECISIONS.has(w.type)).length;
  const setup = model.waiting.filter((w) => w.type === 'getting_started').length;
  const mission = 'Recognize the people behind the mission while the work is still fresh.';
  // A delegate enters for their Sector Lead from the whole sector's pool, shared with the Lead (D1).
  const lead = panel.role === 'delegate' ? (panel.forPerson?.name ?? null) : null;
  let sub: string;
  if (lead !== null) {
    const shared = `You enter awards for ${lead}’s sector. Start an award or follow your own entries below.`;
    sub =
      setup > 0 ? `${shared}${setupSentence(setup, 'Start an award when you’re ready.')}` : shared;
  } else if (panel.nothingYet) {
    const team = panel.recognition.team?.people ?? 0;
    const pool = panel.position?.budget
      ? `Your ${panel.sector.name} pool is set`
      : `Your ${panel.sector.name} pool isn’t set yet`;
    sub = `${pool}${team > 0 ? ` and your team of ${formatCount(team)} is ready` : ''}.${setupSentence(setup, 'Recognize the people behind the mission.')}`;
  } else {
    const parts: string[] = [];
    if (decisions > 0) {
      parts.push(
        decisions === 1
          ? 'One entry needs your OK'
          : `${capitalize(countWord(decisions))} entries need your OK`,
      );
    }
    if (setup > 0) {
      const steps = `${countWord(setup)} quick setup ${setup === 1 ? 'step is' : 'steps are'} waiting`;
      parts.push(parts.length > 0 ? steps : capitalize(steps));
    }
    // APP-148 (the LeadHome board): what was done for them today, in place of the mission line.
    sub =
      done !== null
        ? `${parts.length > 0 ? `${parts.join(', and ')}.` : 'Nothing is waiting on you.'} ${doneTodaySentence(done)}`
        : parts.length > 0
          ? `${parts.join(', and ')}. ${mission}`
          : mission;
  }
  const recognize = recognizeAction(model);
  const insights: HeroAction | null = me.nav.includes('insights')
    ? { label: 'Insights', href: '/insights' }
    : null;
  // D1: a delegate starts a New award for their Sector Lead, or goes to their own entries.
  const [first, second] =
    lead !== null
      ? [
          recognize && { ...recognize, label: 'New award' },
          { label: 'My entries', href: MY_ENTRIES_HREF } satisfies HeroAction,
        ].filter((a): a is HeroAction => a !== null)
      : [recognize, insights].filter((a): a is HeroAction => a !== null);
  return {
    eyebrow:
      lead !== null
        ? `Acting for ${lead} · ${panel.sector.name}`
        : `${panel.sector.name} · ${ROLE_LABEL[panel.role]}`,
    eyebrowTone: 'cyan',
    title: panel.nothingYet ? `Welcome, ${name}.` : `${greeting(now)}, ${name}.`,
    sub,
    ...(first && { actions: second ? ([first, second] as const) : ([first] as const) }),
    stats: [
      ...(panel.role === 'delegate' && panel.position === null
        ? []
        : poolStats(panel.sector.name, panel.fiscalYear, panel.position)),
      ...(done === null || lead !== null ? [] : [doneStat(done)]),
    ],
    photos: peoplePhotos,
  };
}

function electionHero(me: Me, model: HomeModel, award: MyAward, due: string): HomeHero {
  const days = Math.max(0, daysBetween(model.asOf, due));
  const letter = award.documents.find((d) => d.kind === 'letter');
  const election: HeroAction = {
    label: 'Make your election',
    href: `/my-rewards/${award.lineId}/election`,
    tone: 'gold',
  };
  return {
    eyebrow: AWARD_TYPE_LABEL[award.type],
    eyebrowTone: 'gold',
    title: `Congratulations, ${me.person.greetingName}.`,
    sub: `${award.program ? `Recognized for your work on the ${award.program}. ` : ''}Your letter is ready — choose how you’d like to receive your award.`,
    actions: letter ? [election, { label: 'Read your letter', href: letter.href }] : [election],
    stats: [
      {
        label: scoped(award.award.displayId, 'cash portion'),
        value: formatMoney(award.cashCents),
        meta:
          award.options > 0
            ? `Plus ${plural(award.options, 'option', 'options')} · vest over 4 years`
            : 'Cash, stock or a mix: your choice',
      },
      {
        label: scoped(award.award.displayId, 'election window'),
        value: days === 0 ? 'Last day' : `${plural(days, 'day', 'days')} left`,
        meta: `Due ${formatWeekdayDate(due)} · we’ll remind you with 3 days left`,
        tone: 'gold',
      },
    ],
    photos: peoplePhotos,
  };
}

function employeeHero(me: Me, model: HomeModel, now: Date): HomeHero {
  const name = me.person.greetingName;
  const mine = model.mine;
  const open = mine.awards.find((a) => a.stage === 'employee_choice' && a.electionDue !== null);
  if (open?.electionDue) return electionHero(me, model, open, open.electionDue);

  const sector = me.person.sector?.name;
  if (model.nothingYet) {
    const joined = mine.details.joined;
    return {
      eyebrow:
        [sector, joined ? `Joined ${formatDate(joined)}` : null]
          .filter((part): part is string => Boolean(part))
          .join(' · ') || 'Areté MyRewards',
      eyebrowTone: 'gold',
      title: `Welcome to MyRewards, ${name}.`,
      sub: 'This is where Areté recognizes great work. Your awards, letters and choices will all live here, in one place.',
      // FR1: the quick tour is TRN1's walkthrough in practice mode (APP-081).
      actions: [
        { label: 'Take the quick tour', href: QUICK_TOUR_HREF, tone: 'gold', icon: 'arrowRight' },
        { label: 'How recognition works', href: HOW_IT_WORKS_HREF },
      ],
      stats: [],
      photos: peoplePhotos,
    };
  }

  const recognize = recognizeAction(model);
  const myRewards: HeroAction = { label: 'My rewards', href: '/my-rewards' };
  return {
    eyebrow: sector ? `${sector} · Your recognition` : 'Your recognition',
    eyebrowTone: 'gold',
    title: `${greeting(now)}, ${name}.`,
    sub:
      mine.awards.length > 0
        ? 'Your awards, letters and choices, all in one place.'
        : 'When someone recognizes you, it shows here first, with your letter and any cash-or-stock choice.',
    actions: recognize
      ? [{ ...recognize, label: 'Recognize someone', tone: 'gold' }, myRewards]
      : [myRewards],
    stats:
      mine.totals.awards > 0
        ? [
            {
              label: scoped(
                'Your awards',
                mine.totals.since ? `since ${String(mine.totals.since)}` : 'so far',
              ),
              value: plural(mine.totals.awards, 'award', 'awards'),
              meta: `${formatMoney(mine.totals.cashPaidCents)} paid · ${formatCount(mine.totals.options)} options`,
            },
          ]
        : [],
    photos: peoplePhotos,
  };
}

/**
 * The Home hero for this person's role mix: the queue (HOME2), then the Executive (X1/FR3), a
 * sector (HOME1), a requester (R1), and the employee's own (E1/FR1).
 */
export function homeHero(
  me: Me,
  model: HomeModel,
  now: Date,
  acting: HomeActing = NO_ACTING,
): HomeHero {
  if (model.queue) return queueHero(me, model, model.queue, acting.others);
  if (model.executive) return executiveHero(me, model.executive, now, acting.doneToday);
  const panel = model.sectors[0];
  if (panel) return blendedHero(me, model, panel, now, acting.doneToday);
  if (model.requester) return requesterHero(me, model.requester, now);
  return employeeHero(me, model, now);
}
